import { MouseEventHandler, useState } from 'react';
import './App.css';

/********
 * 
 *  Types & Constants 
 * 
*********/

const PIECE_EMPTY = String.fromCharCode(0);
const PIECE_X = 'X' as const;
const PIECE_O = 'O' as const;

const ROW_WIDTH = 3;

const TILE_TOP_LEFT = 0;
const TILE_TOP_MIDDLE = 1;
const TILE_TOP_RIGHT = 2;
const TILE_MIDDLE_LEFT = 3;
const TILE_MIDDLE_MIDDLE = 4;
const TILE_MIDDLE_RIGHT = 5;
const TILE_BOTTOM_LEFT = 6;
const TILE_BOTTOM_MIDDLE = 7;
const TILE_BOTTOM_RIGHT = 8;

const MAX_MOVES = 8; // 0-index. colloquially it is 9.

type GameTileState = typeof PIECE_EMPTY | typeof PIECE_X | typeof PIECE_O;
type GameBoardState = [
  GameTileState, GameTileState, GameTileState,
  GameTileState, GameTileState, GameTileState,
  GameTileState, GameTileState, GameTileState
];
type GameBoardIndex =
  | typeof TILE_TOP_LEFT
  | typeof TILE_TOP_MIDDLE
  | typeof TILE_TOP_RIGHT
  | typeof TILE_MIDDLE_LEFT
  | typeof TILE_MIDDLE_MIDDLE
  | typeof TILE_MIDDLE_RIGHT
  | typeof TILE_BOTTOM_LEFT
  | typeof TILE_BOTTOM_MIDDLE
  | typeof TILE_BOTTOM_RIGHT;
  const GAME_BOARD_FRESH: GameBoardState = [
    PIECE_EMPTY,PIECE_EMPTY,PIECE_EMPTY, 
    PIECE_EMPTY,PIECE_EMPTY,PIECE_EMPTY, 
    PIECE_EMPTY,PIECE_EMPTY,PIECE_EMPTY, 
  ];
type GameTileClickHandler = (tileNumber: GameBoardIndex) => void;

// should probably be an object
type GameOverState = [boolean, GameTileState, GameBoardIndex[]];
const GAME_OVER_I = 0;
const GAME_OVER_WINNER_I = 1;
const GAME_OVER_WIN_PATTERN_I = 2;
const GAME_NOT_OVER: GameOverState = [false, PIECE_EMPTY, []];
const GAME_OVER_TIE: GameOverState = [true, PIECE_EMPTY, []];

/********
 * 
 *  Utilities 
 * 
*********/

function formatGameOver(
  hasWinner: boolean,
  winningPiece: GameTileState,
  winningPattern: GameBoardIndex[]
): GameOverState {
  return [hasWinner, winningPiece, winningPattern];
}

function computePiece(turnNumber: number): typeof PIECE_X | typeof PIECE_O {
  return turnNumber % 2 === 0 ? PIECE_X : PIECE_O;
}

function isGameOver(overState: GameOverState): boolean {
  return overState[GAME_OVER_I];
}

function hasWinner(winState: GameOverState): boolean {
  return winState[GAME_OVER_WINNER_I] !== PIECE_EMPTY;
}

function winstateContainsTile(tile: GameBoardIndex, winState: GameOverState): boolean {
  return winState[GAME_OVER_WIN_PATTERN_I].includes(tile);
}

function walkDown(current: GameBoardIndex): null | GameBoardIndex {
  const next = current + ROW_WIDTH;
  if (next > TILE_BOTTOM_RIGHT) {
    return null; 
  }

  return next as GameBoardIndex;
}

function walkRight(current: GameBoardIndex): null | GameBoardIndex {
  const next = current + 1;
  const currentRow = Math.floor(current / ROW_WIDTH);
  const nextRow = Math.floor(next / ROW_WIDTH);
  if (next > TILE_BOTTOM_RIGHT || currentRow !== nextRow) {
    return null;
  }

  return next as GameBoardIndex;
}

function walkLeftDownRight(current: GameBoardIndex): null | GameBoardIndex {
  const next = current + 4;
  if (next > TILE_BOTTOM_RIGHT) {
    return null; 
  }

  return next as GameBoardIndex;
}

function walkRightDownLeft(current: GameBoardIndex): null | GameBoardIndex {
  const next = current + 2;
  // special because tile 8 is not a valid match here.
  if (next > TILE_BOTTOM_LEFT) {
    return null; 
  }

  return next as GameBoardIndex;
}

function walkFourCorners(current: GameBoardIndex): null | GameBoardIndex {
  const FOUR_CORNERS = [TILE_TOP_LEFT, TILE_TOP_RIGHT, TILE_BOTTOM_LEFT, TILE_BOTTOM_RIGHT] as const;
  const currentIndex = FOUR_CORNERS.findIndex((v) => v === current);
  if (currentIndex == null) {
    console.error('walkFourCorners got lost');
    return null;
  }

  const nextIndex = currentIndex + 1;
  if (nextIndex >= FOUR_CORNERS.length) {
    return null
  }  

  return FOUR_CORNERS[nextIndex];
}

// This is extra and is objectively worse for the four-corners and argubaly diagonal cases than
// enumerating the condition in a bunch of ifs, but functional programming is fun sometimes
// and it feels ok for the walkDown / accross. Makes resizing the board slightly less typing if you wanted to do that.
function isWinningPattern(
  startTile: GameBoardIndex,
  state: GameBoardState,
  nextTile: (current: GameBoardIndex) => null | GameBoardIndex
): GameOverState {
  let winner: GameTileState = state[startTile];
  let index: null | GameBoardIndex = startTile;
  const winningPattern: GameBoardIndex[] = [];

  while (index != null) {
    if (winner !== state[index]) {
      return GAME_NOT_OVER;
    }

    winningPattern.push(index);
    index = nextTile(index);
  }

  return formatGameOver(true, winner, winningPattern);
}

function computeGameWinner(state: GameBoardState): GameOverState {
  // down
  const columnOneWinner = isWinningPattern(TILE_TOP_LEFT, state, walkDown);
  if (hasWinner(columnOneWinner)) {
    return columnOneWinner;
  }

  const columnTwoWinner = isWinningPattern(TILE_TOP_MIDDLE, state, walkDown);
  if (hasWinner(columnTwoWinner)) {
    return columnTwoWinner;
  }

  const columnThreeWinner = isWinningPattern(TILE_TOP_RIGHT, state, walkDown);
  if (hasWinner(columnThreeWinner)) {
    return columnThreeWinner;
  }

  // accross
  const rowOneWinner = isWinningPattern(TILE_TOP_LEFT, state, walkRight);
  if (hasWinner(rowOneWinner)) {
    return rowOneWinner;
  }

  const rowTwoWinner = isWinningPattern(TILE_MIDDLE_LEFT, state, walkRight);
  if (hasWinner(rowTwoWinner)) {
    return rowTwoWinner;
  }

  const rowThreeWinner = isWinningPattern(TILE_BOTTOM_LEFT, state, walkRight);
  if (hasWinner(rowThreeWinner)) {
    return rowThreeWinner;
  }

  // diagonal
  const leftRightWinner = isWinningPattern(TILE_TOP_LEFT, state, walkLeftDownRight);
  if (hasWinner(leftRightWinner)) {
    return leftRightWinner;
  }

  const rightDownLeftWinner = isWinningPattern(TILE_TOP_RIGHT, state, walkRightDownLeft);
  if (hasWinner(rightDownLeftWinner)) {
    return rightDownLeftWinner;
  }

  // special
  const fourCornersWinner = isWinningPattern(TILE_TOP_LEFT, state, walkFourCorners);
  if (hasWinner(fourCornersWinner)) {
    return fourCornersWinner;
  }

  return GAME_NOT_OVER;
}

/********
 * 
 *  Components 
 * 
*********/

function GameTile({
  tileIndex,
  tileState,
  winState,
  onClick,
}: {
  tileIndex: GameBoardIndex;
  tileState: GameTileState;
  winState: GameOverState;
  onClick: MouseEventHandler;
}) {
  const isDisabled = tileState !== PIECE_EMPTY || isGameOver(winState); 
  const className = ['Game-Tile'];
  
  if (isDisabled) {
    className.push('inactive');
  } else {
    className.push('active');
  }

  if (isDisabled && winstateContainsTile(tileIndex, winState)) {
    className.push('winpath')
  }

  return <td className={className.join(' ')} onClick={isDisabled ? undefined : onClick}>{ tileState }</td>;
}

function GameBoard({ gameBoardState, winState, onTileClick }: { gameBoardState: GameBoardState; winState: GameOverState; onTileClick: GameTileClickHandler }) {
  return (
    <table className='Game-Board' cellSpacing={0}>
      <tbody>
        <tr>
          <GameTile
            tileIndex={TILE_TOP_LEFT}
            tileState={gameBoardState[TILE_TOP_LEFT]}
            winState={winState}
            onClick={() => onTileClick(TILE_TOP_LEFT)}
          ></GameTile>
          <GameTile
            tileIndex={TILE_TOP_MIDDLE}
            tileState={gameBoardState[TILE_TOP_MIDDLE]}
            winState={winState}
            onClick={() => onTileClick(TILE_TOP_MIDDLE)}
          ></GameTile>
          <GameTile
            tileIndex={TILE_TOP_RIGHT}
            tileState={gameBoardState[TILE_TOP_RIGHT]}
            winState={winState}
            onClick={() => onTileClick(TILE_TOP_RIGHT)}
          ></GameTile>
        </tr>
        <tr>
          <GameTile
            tileIndex={TILE_MIDDLE_LEFT}
            tileState={gameBoardState[TILE_MIDDLE_LEFT]}
            winState={winState}
            onClick={() => onTileClick(TILE_MIDDLE_LEFT)}
          ></GameTile>
          <GameTile
            tileIndex={TILE_MIDDLE_MIDDLE}
            tileState={gameBoardState[TILE_MIDDLE_MIDDLE]}
            winState={winState}
            onClick={() => onTileClick(TILE_MIDDLE_MIDDLE)}
          ></GameTile>
          <GameTile
            tileIndex={TILE_MIDDLE_RIGHT}
            tileState={gameBoardState[TILE_MIDDLE_RIGHT]}
            winState={winState}
            onClick={() => onTileClick(TILE_MIDDLE_RIGHT)}
          ></GameTile>
        </tr><tr>
          <GameTile
            tileIndex={TILE_BOTTOM_LEFT}
            tileState={gameBoardState[TILE_BOTTOM_LEFT]}
            winState={winState}
            onClick={() => onTileClick(TILE_BOTTOM_LEFT)}
          ></GameTile>
          <GameTile
            tileIndex={TILE_BOTTOM_MIDDLE}
            tileState={gameBoardState[TILE_BOTTOM_MIDDLE]}
            winState={winState}
            onClick={() => onTileClick(TILE_BOTTOM_MIDDLE)}
          ></GameTile>
          <GameTile
            tileIndex={TILE_BOTTOM_RIGHT}
            tileState={gameBoardState[TILE_BOTTOM_RIGHT]}
            winState={winState}
            onClick={() => onTileClick(TILE_BOTTOM_RIGHT)}
          ></GameTile>
        </tr>
      </tbody>
    </table>
  );
}

function App() {
  const [gameWinState, setGameWinState] = useState<GameOverState>(GAME_NOT_OVER);
  const [gameBoardState, setGameBoardState] = useState<GameBoardState>(GAME_BOARD_FRESH);
  const [moveNumber, setMoveNumber] = useState<number>(0);

  function onTileClick(tileNumber: GameBoardIndex) {
    const newBoardState: GameBoardState = [...gameBoardState];
    newBoardState[tileNumber] = computePiece(moveNumber);
    
    const gameWinner = computeGameWinner(newBoardState);
    if (hasWinner(gameWinner)) {
      setGameWinState(gameWinner);
    } else if (moveNumber === MAX_MOVES) {
      // over, no winner
      setGameWinState(GAME_OVER_TIE); 
    }

    setGameBoardState(newBoardState);
    setMoveNumber(moveNumber + 1);
  }

  function resetGame() {
    setGameBoardState(GAME_BOARD_FRESH);
    setGameWinState(GAME_NOT_OVER);
    setMoveNumber(0);
  }

  const winnerText = hasWinner(gameWinState) ? `${gameWinState[1]} Wins!` : `Tie!`;
  return (
    <div className="App">
      <header className="App-header">
        Tic-Tac-Toe
      </header>
      {
        isGameOver(gameWinState)
          ? <h2 className='Announcement ecstatic' onClick={resetGame}>{winnerText} - Try Again?</h2>
          : <h2 className='Announcement standard'>The game is afoot...</h2>
      }
      <div className='Game-Container'>
        <GameBoard gameBoardState={gameBoardState} winState={gameWinState} onTileClick={onTileClick}/>
      </div>
    </div>
  );
}

export default App;
export const unitTest = {
  GAME_OVER_TIE,
  GAME_NOT_OVER,
  GAME_BOARD_FRESH,
  PIECE_EMPTY,
  PIECE_O,
  PIECE_X,
  formatGameOver,

  walkDown,
  walkRight,
  walkLeftDownRight,
  walkRightDownLeft,
  walkFourCorners,
  GameTile,
}